import {useEffect, useState, ReactNode, useMemo} from 'react';
import {useUrl} from '@shopify/hydrogen';
import {ProductVariant} from '@shopify/hydrogen/storefront-api-types';

import QuantityAdjuster from '../QuantityAdjuster.client';
import {useGlobalContainer} from '../../contexts/GlobalContainerProvider.client';
import {TwlAddToCartButton} from '../cart/TwlAddToCartButton.client';

interface QuantityAdjustableBuyTicketWidgetProps {
  children?: ReactNode | Array<ReactNode>;
  shopifyVariant: ProductVariant;
}

export default function QuantityAdjustableBuyTicketWidget({
  shopifyVariant,
  children,
}: QuantityAdjustableBuyTicketWidgetProps) {
  const [quantity, setQuantity] = useState(1);
  const [isCartButtonDisabled, setIsCartButtonDisabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>(null);

  const {pathname} = useUrl();
  useEffect(() => {
    if (pathname) {
      setQuantity(1);
      setIsCartButtonDisabled(true);
      setErrorMessage(null);
    }
  }, [pathname]);

  useEffect(() => {
    if (shopifyVariant) {
      setIsCartButtonDisabled(!shopifyVariant.availableForSale);
    }
  }, [shopifyVariant]);

  useEffect(() => {
    if (shopifyVariant && quantity > shopifyVariant.quantityAvailable) {
      let errorMsg = `Sorry, we only have ${shopifyVariant.quantityAvailable} of these tickets left.`;
      if (shopifyVariant.quantityAvailable === 0) {
        errorMsg = `Sorry, we're sold out.`;
      }
      setErrorMessage(errorMsg);
      setIsCartButtonDisabled(true);
    } else {
      setErrorMessage(null);
      setIsCartButtonDisabled(false);
    }
  }, [shopifyVariant, quantity]);

  const {setIsTicketsWidgetActive} = useGlobalContainer();

  const totalAmount = useMemo(() => {
    if (shopifyVariant && !isCartButtonDisabled) {
      try {
        return Number(shopifyVariant.price.amount) * quantity;
      } catch {
        return null;
      }
    }
    return null;
  }, [shopifyVariant, quantity, isCartButtonDisabled]);

  return (
    <>
      <button
        className="buy-tickets__mobile"
        style={{color: '#ffffff'}}
        id="buyTicketsTrigger"
        onClick={() => setIsTicketsWidgetActive((value) => !value)}
      >
        <span className="buy-tickets-text">
          Buy Tickets <i className="icon-tickets" />
        </span>
        <span className="buy-tickets-close-text"> Close </span>
      </button>
      <div className="buy-tickets" id="buy-tickets">
        <h6 className="buy-tickets__title title-1">Buy tickets</h6>
        <div className="buy-tickets__form">
          <form action="#">
            <div className="quantity-field-label">Quantity</div>
            <QuantityAdjuster
              onChange={setQuantity}
              quantity={quantity}
              containerClassName="buy-tickets_qty"
            />
            {children}
            {errorMessage && (
              <p style={{color: '#f73400', marginTop: '1em'}}>{errorMessage}</p>
            )}
            <div className="form__row mt-30">
              <TwlAddToCartButton
                className="button button_full"
                variant={shopifyVariant}
                quantity={quantity}
                disabled={isCartButtonDisabled}
                accessibleAddingToCartLabel="Adding ticket to your cart"
              >
                {totalAmount ? `Add to cart - $${totalAmount}` : 'Add to cart'}
              </TwlAddToCartButton>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
